import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { rem, Spacer, AppView, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, NavigationHeader } from 'design-system';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Choices from 'design-system/Choices';
import InfoCard from 'design-system/InfoCard';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch } from 'store/hooks';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
const CHOICES = ['Yes', 'No'];
const JISAExistingAccountCheck = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    const scrollY = useSharedValue(0);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const navigateToNextScreen = useNavigateToNextScreen();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const onSelect = useCallback((item) => {
        dispatch({ type: 'SAVE_ONBOARDING_STATE', hasJISAOrTrustFund: item === CHOICES[0] });
        navigateToNextScreen();
    }, [navigateToNextScreen]);
    const handleHowToCheck = useCallback(() => {
        Modal.showConfirmation({
            title: 'Not sure how to check?',
            subtitle: 'Try checking emails from providers you’ve used, account documents in the child’s name, or any Child Trust Fund records.',
            confirmButtonTitle: 'Got it',
        });
    }, []);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    return (<AppView withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Does the child already have a Stocks & shares JISA or Child Trust Fund?" subTitle={<>
              <Text TextThin-16 Secondary>
                We need to check this before opening a new Junior ISA
              </Text>
              <Spacer h={8}/>
              <View style={styles.row}>
                <Text Text-16 Primary suppressHighlighting onPress={handleHowToCheck}>
                  Not sure how to check?
                </Text>
              </View>
            </>}/>

        <View style={styles.choicesContainer}>
          <Choices data={CHOICES} onSelect={onSelect}/>
        </View>

        <Spacer h={16}/>

        <InfoCard>
          <Text TextThin-14 Secondary>
            To open a Junior ISA, your child must not have a Child Trust Fund. If they already have a Junior ISA with
            another provider, you can easily transfer it to Emma once your account is set up.
          </Text>
        </InfoCard>
      </Animated.ScrollView>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    choicesContainer: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
    rightBtnContainer: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default JISAExistingAccountCheck;
